<script lang="ts">
  import { createEditor, setEditorContext } from "svelte-tiptap";
  import { styles, readOnlyExtensions } from "./shared";
  import { generateHTML } from "@tiptap/html";
  import { browser } from "$app/environment";
  import { reportError } from "app/utils/errors";
  import { alerts } from "app/stores/alert";
  import { saveToClipboard } from "app/utils/clipboard";
  import { createMarkdownBlob } from "./Markdown/markdown";
  import type { Readable } from "svelte/store";
  import type { Editor, JSONContent } from "@tiptap/core";
  import "./extensions/codeblock.css";

  export let readerMode = true;
  export let content: JSONContent | string;
  export let isDocument = false;
  // If we have a Reader embedded within some card, for SEO purposes we only care about linking through to whatever the
  // card links to, rather than the links inside the Reader. Once the content is loaded on the client, however, we want
  // links to be recreated properly.
  export let disableLinksInSSR = false;
  export let disableLinkHandle = false;

  let element: Element;
  let editor: Readable<Editor>;

  setEditorContext();

  function recreateEditor(element: Element) {
    editor = createEditor({
      element,
      editable: false,
      content: content,
      extensions: readOnlyExtensions(false, isDocument, "", disableLinkHandle),
      editorProps: {
        attributes: {
          // allow browsers to translate content (read-only - ssr content allows all ready)
          translate: "yes",
          class: `${styles} ${readerMode ? "max-w-none" : "m-auto"}`,
        },
      },
    });
  }

  $: if (element) {
    recreateEditor(element);
  }

  // attempt to render html in SSR
  let ssrHTML = "";
  try {
    if (!browser && typeof content !== "string") {
      ssrHTML = generateHTML(
        content,
        readOnlyExtensions(false, isDocument, "", disableLinkHandle, disableLinksInSSR, true)
      );
    }
  } catch (e) {
    // this should not really happen often, but just in case
    reportError(e as Error);
  }

  // it's mysterious why this works. We rely on a `key` block to rerender the editor when content changes.
  // But simply using `content` results in the block being rendered twice on first load, once after SSR, and
  // once again after hydration.
  let storedContent: any = content;
  // You'd think that this was equivalent to the commented out line below, but apparently is not
  // storedContent = content;
  $: if (content !== storedContent) {
    storedContent = content;
  }

  // EXPORT TO MARKDOWN FUNCTIONALITY
  export const copyMarkdown = () => {
    let markdown = $editor.storage.markdown.getMarkdown();

    if (markdown) {
      saveToClipboard(markdown, alerts.add, "Content copied to clipboard");
    } else {
      alerts.error("No content to copy");
    }
  };

  export const downloadMarkdown = (title: string) => {
    let markdown = $editor.storage.markdown.getMarkdown();

    if (markdown) {
      try {
        createMarkdownBlob(title, markdown);
        alerts.success("Downloaded .md file");
      } catch {
        alerts.error("Failed to download .md file");
      }
    } else {
      alerts.error("No content to download");
    }
  };
</script>

{#key storedContent}
  {#if browser}
    <div class="editor" bind:this={element} />
  {:else if ssrHTML}
    <div class="editor {styles} {readerMode ? 'max-w-none' : 'm-auto'}">
      {@html ssrHTML}
    </div>
  {/if}
{/key}

<style lang="postcss">
  /* Basic editor styles */
  .editor :global .ProseMirror {
    pre {
      font-family: "JetBrainsMono", monospace;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;

      code {
        color: inherit;
        padding: 0;
        background: none;
        font-size: 0.8rem;
      }
    }

    code {
      word-break: break-word;
    }

    ul {
      list-style-type: disc;
    }
    ul ul {
      list-style-type: circle;
    }
    ul ul ul {
      list-style-type: square;
    }
    ul ul ul ul {
      list-style-type: disc;
    }
    ul ul ul ul ul {
      list-style-type: circle;
    }
    ul ul ul ul ul ul {
      list-style-type: square;
    }

    ol {
      list-style-type: decimal;
    }
    ol ol {
      list-style-type: lower-alpha;
    }
    ol ol ol {
      list-style-type: lower-roman;
    }
    ol ol ol ol {
      list-style-type: decimal;
    }
    ol ol ol ol ol {
      list-style-type: lower-alpha;
    }
    ol ol ol ol ol ol {
      list-style-type: lower-roman;
    }

    img {
      max-width: 100%;
      height: auto;
    }

    blockquote {
      padding-left: 1rem;
      border-left-color: #898e9b;
    }

    /* Note: Accordion styles also in Composer.svelte */
    /* Placeholder for accordion content */
    .accordion-content > .is-empty::before {
      content: "Add some content" !important;
      height: 0;
      float: left;
      pointer-events: none;
    }
    .accordion-content {
      opacity: 1;
      animation: fade-in 0.2s linear forwards;
    }

    /* Accordion toggle */
    .toggle-button {
      /* target direct child button only to avoid conflict with any other nested blocks in the toggle */
      > button:first-of-type {
        border-radius: 5px;
        margin-right: 6px;
        width: 28px;
        height: 28px;

        &:focus-visible {
          outline: 2px solid #8aa3fc;
        }

        &::before {
          display: flex;
          justify-content: center;
          align-items: center;
          content: "▸";
          width: 28px;
          height: 28px;
          transition: transform 0.2s ease-in-out;

          /* hover colours defined as utilities in app.css to be able to use with dark mode state */
        }
      }
    }

    .accordion {
      &.is-open > button::before {
        transform: rotate(90deg);
        transition: transform 0.2s ease-in-out;
      }

      > div {
        width: 100%;
      }
    }
    /* Table block */
    /* border and text colours configured in app.css */
    table {
      border-collapse: separate;
      border-spacing: 0;
      margin: 0;
      overflow-y: hidden;
      table-layout: fixed;
      width: min-content; /* required to prevent table from stretching to full width of editor */

      /* header first cell */
      th:first-child {
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
      }

      th {
        text-align: left;
        font-weight: 500;
        z-index: 3;
        box-sizing: border-box;
        padding: 8px 4px;
        position: relative;
        vertical-align: top;

        > * {
          margin-bottom: 0;
        }
      }

      /* header last cell */
      th:last-child {
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
      }

      /* main table cells */
      td {
        box-sizing: border-box;
        position: relative;
        vertical-align: top;

        > * {
          margin-bottom: 0;
        }
      }

      p {
        margin: 0 4px;
      }

      .selectedCell:after {
        content: "";
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        pointer-events: none;
        position: absolute;
        z-index: 2;
      }

      .column-resize-handle {
        background-color: transparent;
        position: absolute;
        bottom: -2px;
        right: -2px;
        top: 0;
        width: 6px;
        height: 100%;
        cursor: ew-resize;
        z-index: 3;
      }
    }
    .resize-cursor {
      cursor: ew-resize;
    }
  }
</style>
